/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';

const SEP = '· ·';
const DEFAULT_BACKGROUND_COLOR = '#1A1A1A';
const DEFAULT_TEXT_COLOR = '#FFFFFF';
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const parseHexColor = (value: string | undefined, fallback: string): string => {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : fallback;
};

@Component('scrollingBar', {
    name: 'Scrolling Bar',
    group: 'Content',
    description: 'Marquee ticker bar with up to 7 independently authored messages separated by decorative dividers',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ScrollingBarMetadata {
    @AttributeDefinition({ name: 'Message 1', type: 'string' })
    message1!: string;

    @AttributeDefinition({ name: 'Message 2', type: 'string', required: false })
    message2?: string;

    @AttributeDefinition({ name: 'Message 3', type: 'string', required: false })
    message3?: string;

    @AttributeDefinition({ name: 'Message 4', type: 'string', required: false })
    message4?: string;

    @AttributeDefinition({ name: 'Message 5', type: 'string', required: false })
    message5?: string;

    @AttributeDefinition({ name: 'Message 6', type: 'string', required: false })
    message6?: string;

    @AttributeDefinition({ name: 'Message 7', type: 'string', required: false })
    message7?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color',
        description: 'Hex color for the bar background, e.g. #1A1A1A or #000.',
        type: 'string',
        defaultValue: DEFAULT_BACKGROUND_COLOR,
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'textColor',
        name: 'Text Color',
        description: 'Hex color for the message text and dividers, e.g. #FFFFFF or #fff.',
        type: 'string',
        defaultValue: DEFAULT_TEXT_COLOR,
    })
    textColor?: string;
}

interface ScrollingBarProps {
    message1: string;
    message2?: string;
    message3?: string;
    message4?: string;
    message5?: string;
    message6?: string;
    message7?: string;
    backgroundColor?: string;
    textColor?: string;
}

export default function ScrollingBar({
    message1,
    message2,
    message3,
    message4,
    message5,
    message6,
    message7,
    backgroundColor,
    textColor,
}: ScrollingBarProps) {
    const items = [message1, message2, message3, message4, message5, message6, message7].filter(
        (m): m is string => typeof m === 'string' && m.trim().length > 0
    );

    if (items.length === 0) return null;

    // Three passes for a seamless marquee loop; each entry carries a stable per-pass key.
    const repeated = [0, 1, 2].flatMap((pass) => items.map((item) => ({ item, key: `${pass}-${item}` })));
    const colors = {
        backgroundColor: parseHexColor(backgroundColor, DEFAULT_BACKGROUND_COLOR),
        color: parseHexColor(textColor, DEFAULT_TEXT_COLOR),
    };

    return (
        <div className="overflow-hidden py-3 select-none" style={colors} aria-hidden="true">
            <div className="flex animate-df-ticker whitespace-nowrap">
                {repeated.map(({ item, key }) => (
                    <span key={key} className="inline-flex items-center gap-5 px-5 text-sm font-medium tracking-wide">
                        {item}
                        <span className="opacity-40">{SEP}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
